import abstractPlayer from "./player/abstract";
import accountToggle from "./account-toggle";
import adjustExternalLinks from "./adjust-external-links";
import ajaxFlash from "./ajax-flash";
import confirmAction from "./confirm-action";
import csrfTag from "./csrf";
import editContent from "./edit-content";
import fetchAd from "./views/fetch-ad";
import flash from "./flash";
import mailchimpForm from "./mailchimp-form";
import mastheadPlayer from "./masthead-player";
import playable from "./playable";
import player from "./player";
import revealMobileNav from "./reveal-mobile-nav";
import toggleClass from "./toggle-class";
import waitForCsrf from "./wait-for-csrf";
import donationRadio from "./donation-radio";

/**
 * Defer importing a viewloader view until it's called
 *
 * There are some limitations in webpack around dynamic expressions, and so we
 * need to have the view base be a known path like `../`
 *
 * https://webpack.js.org/guides/migrating/#dynamic-expressions
 *
 * @param {String} path Path to view module (relative to entry root)
 */
type View = void | { destroy(): void };
type ViewFunc = (el: HTMLElement, props: any) => (View | Promise<View>);
type ViewModule = { default: ViewFunc };
function deferredView(dynamicImport: () => Promise<ViewModule>): ViewFunc {
  return (el, props) => {
    return dynamicImport().then(f => f.default(el, props));
  };
}

type ViewMap = { [viewName: string]: ViewFunc };

/**
 * Viewloader-compatible view functions
 * https://github.com/icelab/viewloader
 */
export default {
  // must be initialized first as it clones element and replaces in DOM for some reason?
  toggleClass,

  // init the rest in whatever order:
  abstractPlayer,
  flash,
  csrfTag,
  waitForCsrf,
  fetchAd,
  adjustExternalLinks,
  donationRadio,
  animateBackground: deferredView(() => import("./animate-background")),
  calendar: deferredView(() => import("./calendar")),
  characterCountdown: deferredView(() => import("./character-countdown")),
  colorFromImage: deferredView(() => import("./color-from-image")),
  subscriberGiveawayEnterButton: deferredView(() => import("./subscriber-giveaway-enter-button")),
  copyToClipboard: deferredView(() => import("./copy-to-clipboard")),
  editContent,
  eventFilters: deferredView(() => import("./event-filters")),
  searchFilters: deferredView(() => import("./search-filters")),
  inViewport: deferredView(() => import("./in-viewport")),
  mapEmbed: deferredView(() => import("./map-embed")),
  player,
  scheduleCalendar: deferredView(() => import("./schedule-calendar")),
  accountSubscriptionFetch: deferredView(() => import("./account-subscription-fetch")),
  accountToggle,
  ajaxFlash,
  confirmAction,
  creditCardForm: deferredView(() => import("./credit-card-form")),
  historyProgress: deferredView(() => import("./history-progress")),
  playable,
  mastheadPlayer,
  mailchimpForm,
  onAirToggle: deferredView(() => import("./onair-toggle")),
  pausePlayerOnClick: deferredView(() => import("./pause-player-on-click")),
  responsiveEmbed: deferredView(() => import("./responsive-embed")),
  revealMobileNav,
  subscriptionProcessingCheck: deferredView(() => import("./subscription-processing-check")),
  timeslotList: deferredView(() => import("./timeslot-list")),
  toggleCardForm: deferredView(() => import("./toggle-card-form")),
  toggleInternationalAddress: deferredView(() => import("./toggle-international-address")),
  triggerLiveStream: deferredView(() => import("./trigger-live-stream")),
  specialMode: deferredView(() => import("./special-mode")),
  toggleGenderSelfDescribed: deferredView(() => import("./toggle-gender-self-described")),
} satisfies ViewMap;
